import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  BrainCircuit, Pause, Play, ShieldAlert, ShieldCheck, Target, Loader2,
  CheckCircle2, XCircle, Clock, Zap, ListChecks, ScrollText, Bell, Database,
  AlertTriangle, ChevronRight, Plus, Gauge,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_STYLE = {
  low: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  medium: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  high: 'text-red-400 bg-red-500/10 border-red-500/20',
};

const STATUS_STYLE = {
  queued: 'text-sky-400 bg-sky-500/10',
  awaiting_approval: 'text-amber-400 bg-amber-500/10',
  running: 'text-orange-400 bg-orange-500/10',
  completed: 'text-emerald-400 bg-emerald-500/10',
  failed: 'text-red-400 bg-red-500/10',
  rejected: 'text-zinc-400 bg-zinc-500/10',
  cancelled: 'text-zinc-500 bg-zinc-500/10',
  escalated: 'text-purple-400 bg-purple-500/10',
};

const StatCard = ({ label, value, testid }) => (
  <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4" data-testid={testid}>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-zinc-500 mt-1">{label}</p>
  </div>
);

const StatusPill = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${STATUS_STYLE[status] || 'text-zinc-400 bg-zinc-500/10'}`}>
    {status?.replace('_', ' ')}
  </span>
);

export default function AutonomyCenterPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [meta, setMeta] = useState(null);
  const [overview, setOverview] = useState(null);
  const [goals, setGoals] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [audit, setAudit] = useState([]);
  const [events, setEvents] = useState([]);
  const [memory, setMemory] = useState([]);
  const [activeTab, setActiveTab] = useState('tasks');
  const [busy, setBusy] = useState(null);
  const [planningGoal, setPlanningGoal] = useState(null);
  const [newGoalOpen, setNewGoalOpen] = useState(false);
  const [goalTitle, setGoalTitle] = useState('');
  const [goalDesc, setGoalDesc] = useState('');
  const [goalMetric, setGoalMetric] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [ov, gl, ap, tk, au, ev, me] = await Promise.all([
        axios.get(`${API}/autonomy/overview`, { headers }),
        axios.get(`${API}/autonomy/goals`, { headers }),
        axios.get(`${API}/autonomy/approvals`, { headers }),
        axios.get(`${API}/autonomy/tasks`, { headers }),
        axios.get(`${API}/autonomy/audit?limit=30`, { headers }),
        axios.get(`${API}/autonomy/events?limit=30`, { headers }),
        axios.get(`${API}/autonomy/memory?limit=30`, { headers }),
      ]);
      setOverview(ov.data); setGoals(gl.data); setApprovals(ap.data);
      setTasks(tk.data); setAudit(au.data); setEvents(ev.data); setMemory(me.data);
    } catch { /* transient */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    axios.get(`${API}/autonomy/meta`).then(r => setMeta(r.data)).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 15000);
    return () => clearInterval(iv);
  }, [refresh]);

  const settings = overview?.settings;

  const setLevel = async (level) => {
    try {
      const { data } = await axios.put(`${API}/autonomy/settings`, { autonomy_level: level }, { headers });
      setOverview(o => ({ ...o, settings: data }));
      toast.success(`Autonomy level set to ${level} — ${meta?.levels?.[level]?.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update level'); }
  };

  const togglePermission = async (cat, value) => {
    try {
      const { data } = await axios.put(`${API}/autonomy/settings`, { permissions: { [cat]: value } }, { headers });
      setOverview(o => ({ ...o, settings: data }));
    } catch { toast.error('Failed to update permission'); }
  };

  const togglePause = async () => {
    const action = settings?.paused ? 'resume' : 'pause';
    try {
      await axios.post(`${API}/autonomy/${action}`, {}, { headers });
      setOverview(o => ({ ...o, settings: { ...o.settings, paused: action === 'pause' } }));
      toast.success(action === 'pause' ? 'PAUSE DELPHI activated — all autonomous execution stopped' : 'Delphi resumed');
      refresh();
    } catch { toast.error(`Failed to ${action}`); }
  };

  const createGoal = async () => {
    if (!goalTitle.trim()) return toast.error('Goal title required');
    try {
      await axios.post(`${API}/autonomy/goals`, { title: goalTitle, description: goalDesc, target_metric: goalMetric }, { headers });
      toast.success('Goal created — click "Plan" to generate tasks');
      setNewGoalOpen(false); setGoalTitle(''); setGoalDesc(''); setGoalMetric('');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create goal'); }
  };

  const planGoal = async (goalId) => {
    setPlanningGoal(goalId);
    try {
      const { data } = await axios.post(`${API}/autonomy/goals/${goalId}/plan`, {}, { headers });
      toast.success(`Plan created: ${data.tasks_created} tasks`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Planning failed'); }
    finally { setPlanningGoal(null); }
  };

  const executeTask = async (taskId) => {
    setBusy(taskId);
    try {
      const { data } = await axios.post(`${API}/autonomy/tasks/${taskId}/execute`, {}, { headers });
      toast.success(data.status === 'completed' ? 'Task executed' : `Task ${data.status}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Execution failed'); }
    finally { setBusy(null); }
  };

  const resolveApproval = async (id, verb) => {
    setBusy(id);
    try {
      await axios.post(`${API}/autonomy/approvals/${id}/${verb}`, {}, { headers });
      toast.success(verb === 'approve' ? 'Approved & executed' : 'Rejected');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };

  if (!overview || !meta) {
    return (
      <AppShell>
        <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center" data-testid="autonomy-loading">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      </AppShell>
    );
  }

  const counts = overview.task_counts || {};

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white pb-16" data-testid="autonomy-center">
        {/* Header */}
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <BrainCircuit className="w-6 h-6 text-orange-400" />
              <div>
                <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="autonomy-title">Autonomy Center</h1>
                <p className="text-sm text-white/40">Delphi Autonomous Core — goals, permissions, approvals & audit</p>
              </div>
            </div>
            <Button
              onClick={togglePause}
              data-testid="pause-delphi-btn"
              className={settings.paused
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white font-bold'
                : 'bg-red-600 hover:bg-red-700 text-white font-bold'}
            >
              {settings.paused ? <><Play className="w-4 h-4 mr-2" />RESUME DELPHI</> : <><Pause className="w-4 h-4 mr-2" />PAUSE DELPHI</>}
            </Button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {settings.paused && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3" data-testid="paused-banner">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
              <p className="text-sm text-red-300">Delphi is PAUSED. All autonomous execution is stopped; state is preserved. Resume to continue.</p>
            </div>
          )}

          {/* Autonomy Level */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5" data-testid="autonomy-level-section">
            <div className="flex items-center gap-2 mb-4">
              <Gauge className="w-4 h-4 text-orange-400" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Autonomy Level</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
              {Object.entries(meta.levels).map(([lvl, info]) => {
                const n = parseInt(lvl);
                const active = settings.autonomy_level === n;
                return (
                  <button key={lvl} onClick={() => setLevel(n)} data-testid={`level-btn-${lvl}`}
                    className={`text-left p-3 rounded-xl border transition-all ${active
                      ? 'border-orange-500/60 bg-orange-500/10'
                      : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'}`}>
                    <p className={`text-xs font-bold ${active ? 'text-orange-400' : 'text-white'}`}>L{lvl} · {info.name}</p>
                    <p className="text-[10px] text-zinc-500 mt-1 leading-snug">{info.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Active Goals" value={overview.goals_active} testid="stat-goals" />
            <StatCard label="Pending Approvals" value={overview.pending_approvals} testid="stat-approvals" />
            <StatCard label="Tasks Completed" value={counts.completed || 0} testid="stat-completed" />
            <StatCard label="Tasks Failed" value={counts.failed || 0} testid="stat-failed" />
            <StatCard label="Actions Taken" value={overview.actions_total} testid="stat-actions" />
            <StatCard label="Avg Confidence" value={overview.avg_confidence} testid="stat-confidence" />
          </div>

          {/* Pending Approvals */}
          {approvals.length > 0 && (
            <div className="bg-amber-500/[0.06] border border-amber-500/20 rounded-2xl p-5" data-testid="approvals-section">
              <div className="flex items-center gap-2 mb-4">
                <ShieldAlert className="w-4 h-4 text-amber-400" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-400">Pending Approvals ({approvals.length})</h2>
              </div>
              <div className="space-y-2">
                {approvals.map(ap => (
                  <div key={ap.id} className="flex flex-wrap items-center gap-3 p-3 bg-zinc-900/60 rounded-xl" data-testid={`approval-${ap.id}`}>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${RISK_STYLE[ap.risk]}`}>{ap.risk} risk</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">{ap.title}</p>
                      <p className="text-xs text-zinc-500 truncate">{ap.reason}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => resolveApproval(ap.id, 'approve')} disabled={busy === ap.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" data-testid={`approve-${ap.id}`}>
                        {busy === ap.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3 mr-1" />Approve</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => resolveApproval(ap.id, 'reject')} disabled={busy === ap.id}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8" data-testid={`reject-${ap.id}`}>
                        <XCircle className="w-3 h-3 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Goals */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5" data-testid="goals-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-orange-400" />
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Goals</h2>
                </div>
                <Button size="sm" onClick={() => setNewGoalOpen(!newGoalOpen)} data-testid="new-goal-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white h-8">
                  <Plus className="w-3 h-3 mr-1" />New Goal
                </Button>
              </div>
              {newGoalOpen && (
                <div className="mb-4 p-3 bg-zinc-800/40 rounded-xl space-y-2" data-testid="new-goal-form">
                  <Input value={goalTitle} onChange={e => setGoalTitle(e.target.value)} placeholder="Goal — e.g. Grow my Instagram engagement"
                    className="bg-zinc-900 border-zinc-700" data-testid="goal-title-input" />
                  <Textarea value={goalDesc} onChange={e => setGoalDesc(e.target.value)} placeholder="Details (optional)" rows={2}
                    className="bg-zinc-900 border-zinc-700" data-testid="goal-desc-input" />
                  <Input value={goalMetric} onChange={e => setGoalMetric(e.target.value)} placeholder="Target metric (optional) — e.g. 3 reels/week"
                    className="bg-zinc-900 border-zinc-700" data-testid="goal-metric-input" />
                  <Button size="sm" onClick={createGoal} className="bg-orange-500 hover:bg-orange-600 text-white w-full" data-testid="create-goal-btn">
                    Create Goal
                  </Button>
                </div>
              )}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {goals.length === 0 && <p className="text-sm text-zinc-500 py-4 text-center" data-testid="goals-empty">No goals yet — create one and Delphi will plan it.</p>}
                {goals.filter(g => g.status !== 'archived').map(g => (
                  <div key={g.id} className="p-3 bg-zinc-800/40 rounded-xl" data-testid={`goal-${g.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-white truncate">{g.title}</p>
                      <StatusPill status={g.status} />
                    </div>
                    {g.plan_summary && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{g.plan_summary}</p>}
                    <div className="flex items-center gap-2 mt-2">
                      {(g.status === 'draft' || g.status === 'failed') && (
                        <Button size="sm" onClick={() => planGoal(g.id)} disabled={planningGoal === g.id}
                          className="bg-white/[0.08] hover:bg-white/[0.15] text-white h-7 text-xs" data-testid={`plan-goal-${g.id}`}>
                          {planningGoal === g.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                          Plan with Delphi
                        </Button>
                      )}
                      <span className="text-[10px] text-zinc-600">Cost used: {g.spent_cost_credits || 0} credits</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Permissions */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5" data-testid="permissions-section">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-orange-400" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Action Permissions</h2>
              </div>
              <p className="text-xs text-zinc-600 mb-4">Grant categories Delphi may execute autonomously (Level 3+). High-risk actions always require approval unless explicitly granted.</p>
              <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
                {Object.entries(meta.categories).map(([cat, info]) => (
                  <div key={cat} className="flex items-center justify-between py-2 border-b border-zinc-800/50 last:border-0" data-testid={`perm-row-${cat}`}>
                    <div className="min-w-0 flex-1 mr-3">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-white">{info.label}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${RISK_STYLE[info.risk]}`}>{info.risk}</span>
                      </div>
                      <p className="text-[11px] text-zinc-600 truncate">{info.desc}</p>
                    </div>
                    <Switch checked={!!settings.permissions?.[cat]} onCheckedChange={v => togglePermission(cat, v)}
                      data-testid={`perm-toggle-${cat}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Activity tabs */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5" data-testid="activity-section">
            <div className="flex flex-wrap gap-2 mb-4">
              {[['tasks', ListChecks, `Tasks (${tasks.length})`], ['audit', ScrollText, 'Audit Log'], ['events', Bell, 'Events'], ['memory', Database, 'Memory']].map(([id, Icon, label]) => (
                <button key={id} onClick={() => setActiveTab(id)} data-testid={`tab-${id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeTab === id ? 'bg-orange-500/15 text-orange-400' : 'text-zinc-500 hover:text-white'}`}>
                  <Icon className="w-3.5 h-3.5" />{label}
                </button>
              ))}
            </div>

            {activeTab === 'tasks' && (
              <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="tasks-list">
                {tasks.length === 0 && <p className="text-sm text-zinc-500 py-4 text-center">No tasks yet — plan a goal to create tasks.</p>}
                {tasks.map(t => (
                  <div key={t.id} className="p-3 bg-zinc-800/40 rounded-xl" data-testid={`task-${t.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill status={t.status} />
                      <p className="text-sm font-medium text-white flex-1 min-w-0 truncate">{t.title}</p>
                      <span className="text-[10px] text-zinc-500">conf {Math.round((t.confidence_score || 0) * 100)}%</span>
                      <span className="text-[10px] text-zinc-500">impact: {t.estimated_impact}</span>
                      {(t.status === 'queued' || t.status === 'failed') && !settings.paused && (
                        <Button size="sm" onClick={() => executeTask(t.id)} disabled={busy === t.id}
                          className="bg-white/[0.08] hover:bg-white/[0.15] text-white h-7 text-xs" data-testid={`exec-task-${t.id}`}>
                          {busy === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Play className="w-3 h-3 mr-1" />Run</>}
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">{t.reason}</p>
                    {t.actual_result?.content && (
                      <details className="mt-2">
                        <summary className="text-[11px] text-orange-400 cursor-pointer">View result</summary>
                        <pre className="text-[11px] text-zinc-400 whitespace-pre-wrap mt-1 max-h-40 overflow-y-auto bg-black/30 rounded-lg p-2">{t.actual_result.content}</pre>
                      </details>
                    )}
                    {t.actual_result?.url && (
                      <a href={t.actual_result.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-orange-400 mt-1 inline-flex items-center gap-1">
                        View artifact <ChevronRight className="w-3 h-3" />
                      </a>
                    )}
                    {t.evaluation && <p className="text-[10px] text-zinc-600 mt-1">Evaluation: {(t.evaluation.score * 10).toFixed(1)}/10 — {t.evaluation.verdict}</p>}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'audit' && (
              <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="audit-list">
                {audit.length === 0 && <p className="text-sm text-zinc-500 py-4 text-center">No autonomous actions recorded yet.</p>}
                {audit.map(a => (
                  <div key={a.id} className="flex items-center gap-3 py-2 border-b border-zinc-800/50 last:border-0 text-xs" data-testid={`audit-${a.id}`}>
                    {a.status === 'completed' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                    <span className="text-zinc-400 shrink-0">{new Date(a.timestamp).toLocaleString()}</span>
                    <span className="text-white font-medium">{a.action_type}</span>
                    <span className="text-zinc-500 truncate flex-1">{a.reason}</span>
                    <span className="text-zinc-600 shrink-0">by {a.agent_id} · {a.authorized_by} · L{a.authorization_level} · {a.duration_ms}ms</span>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'events' && (
              <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="events-list">
                {events.length === 0 && <p className="text-sm text-zinc-500 py-4 text-center">No events yet.</p>}
                {events.map(ev => (
                  <div key={ev.id} className="flex items-center gap-3 py-2 border-b border-zinc-800/50 last:border-0 text-xs">
                    <Clock className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                    <span className="text-zinc-500 shrink-0">{new Date(ev.created_at).toLocaleString()}</span>
                    <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[10px] shrink-0">{ev.event_type}</span>
                    <span className="text-zinc-300 truncate">{ev.message}</span>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'memory' && (
              <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="memory-list">
                {memory.length === 0 && <p className="text-sm text-zinc-500 py-4 text-center">Delphi hasn't stored any memory yet.</p>}
                {memory.map(m => (
                  <div key={m.id} className="flex items-start gap-3 py-2 border-b border-zinc-800/50 last:border-0 text-xs">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] shrink-0 ${m.kind === 'preference' ? 'bg-purple-500/10 text-purple-400' : m.kind === 'decision' ? 'bg-sky-500/10 text-sky-400' : 'bg-zinc-800 text-zinc-400'}`}>{m.kind}</span>
                    <span className="text-zinc-300">{m.content}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
